import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Icon } from '@/components/Icon';

interface LeadField {
  key: string;
  label: string;
  prompt: string;
  category: string;
  required: boolean;
  type: string;
  options?: string[];
}

interface Template {
  template_key: string;
  template_name: string;
  template_description: string;
  business_type: string;
  fields: LeadField[];
}

export const LeadCaptureTab: React.FC = () => {
  const { t } = useTranslation();
  const [fields, setFields] = useState<LeadField[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState<LeadField | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);

  const [newField, setNewField] = useState<LeadField>({
    key: '',
    label: '',
    prompt: '',
    category: 'general',
    required: false,
    type: 'text',
  });

  const getTemplateDisplayName = (template: Template) => {
    const translatedName = t(`leadFields.template_${template.template_key}`);
    if (translatedName !== `leadFields.template_${template.template_key}`) {
      return translatedName;
    }
    return template.template_name;
  };

  const getTemplateDescription = (template: Template) => {
    const translatedDesc = t(`leadFields.template_${template.template_key}_desc`);
    if (translatedDesc !== `leadFields.template_${template.template_key}_desc`) {
      return translatedDesc;
    }
    return template.template_description;
  };

  const categories = [
    { value: 'contacto', label: t('leadFields.cat_contact') || 'Contact' },
    { value: 'producto', label: t('leadFields.cat_product') || 'Product' },
    { value: 'servicio', label: t('leadFields.cat_service') || 'Service' },
    { value: 'proyecto', label: t('leadFields.cat_project') || 'Project' },
    { value: 'equipo', label: t('leadFields.cat_equipment') || 'Equipment' },
    { value: 'logistica', label: t('leadFields.cat_logistics') || 'Logistics' },
    { value: 'pedido', label: t('leadFields.cat_order') || 'Order' },
    { value: 'comercial', label: t('leadFields.cat_commercial') || 'Commercial' },
    { value: 'perfil', label: t('leadFields.cat_profile') || 'Profile' },
    { value: 'general', label: t('leadFields.cat_general') || 'General' },
  ];

  const fieldTypes = [
    { value: 'text', label: t('leadFields.type_text') || 'Text' },
    { value: 'textarea', label: t('leadFields.type_textarea') || 'Long text' },
    { value: 'email', label: t('leadFields.type_email') || 'Email' },
    { value: 'tel', label: t('leadFields.type_tel') || 'Phone' },
    { value: 'number', label: t('leadFields.type_number') || 'Number' },
    { value: 'date', label: t('leadFields.type_date') || 'Date' },
    { value: 'select', label: t('leadFields.type_select') || 'Select' },
    { value: 'checkbox', label: t('leadFields.type_checkbox') || 'Checkbox' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [fieldsRes, templatesRes] = await Promise.all([
        api.get('/lead-fields/config'),
        api.get('/lead-fields/templates'),
      ]);

      if (fieldsRes.data.success) {
        const fieldsData = fieldsRes.data.data?.fields || fieldsRes.data.data || [];
        const mappedFields = fieldsData.map((f: any) => ({
          key: f.field_key,
          label: f.field_label,
          prompt: f.field_prompt,
          category: f.field_category,
          required: f.is_required,
          type: f.field_type,
          options: f.options,
        }));
        setFields(mappedFields);
        setSelectedTemplate(fieldsRes.data.data?.templateKey || '');
      }
      if (templatesRes.data.success) {
        setTemplates(templatesRes.data.data);
      }
    } catch (error) {
      console.error('Failed to load lead fields config:', error);
      showNotification(t('leadFields.loadError') || 'Failed to load configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleApplyTemplate = () => {
    if (!selectedTemplate) return;
    setShowTemplateConfirm(true);
  };

  const confirmApplyTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      setShowTemplateConfirm(false);
      await api.post('/lead-fields/apply-template', { templateKey: selectedTemplate });
      showNotification(t('leadFields.templateApplied') || 'Template applied successfully', 'success');
      await loadData();
    } catch (error) {
      console.error('Failed to apply template:', error);
      showNotification(t('leadFields.templateError') || 'Failed to apply template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveField = async () => {
    if (!newField.key || !newField.label || !newField.prompt) {
      showNotification(t('leadFields.requiredFields') || 'Please fill all required fields', 'error');
      return;
    }

    const updatedFields = editingField
      ? fields.map((f) => (f.key === editingField.key ? newField : f))
      : [...fields, newField];

    try {
      setSaving(true);
      await api.post('/lead-fields/config', { fields: updatedFields });
      showNotification(
        editingField
          ? t('leadFields.fieldUpdated') || 'Field updated'
          : t('leadFields.fieldAdded') || 'Field added',
        'success'
      );
      setFields(updatedFields);
      setShowAddModal(false);
      setEditingField(null);
      setNewField({ key: '', label: '', prompt: '', category: 'general', required: false, type: 'text' });
    } catch (error) {
      console.error('Failed to save field:', error);
      showNotification(t('leadFields.saveError') || 'Failed to save field', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (fieldKey: string) => {
    const updatedFields = fields.filter((f) => f.key !== fieldKey);

    try {
      setSaving(true);
      await api.post('/lead-fields/config', { fields: updatedFields });
      setFields(updatedFields);
      showNotification(t('leadFields.fieldDeleted') || 'Field deleted', 'success');
    } catch (error) {
      console.error('Failed to delete field:', error);
      showNotification(t('leadFields.deleteError') || 'Failed to delete field', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveField = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === fields.length - 1) return;

    const newFields = [...fields];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[swapIndex]] = [newFields[swapIndex], newFields[index]];

    try {
      setSaving(true);
      await api.post('/lead-fields/config', { fields: newFields });
      setFields(newFields);
    } catch (error) {
      console.error('Failed to reorder fields:', error);
      showNotification(t('leadFields.reorderError') || 'Failed to save field order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (field: LeadField) => {
    setEditingField(field);
    setNewField({ ...field });
    setShowAddModal(true);
  };

  const openAddModal = () => {
    setEditingField(null);
    setNewField({ key: '', label: '', prompt: '', category: 'general', required: false, type: 'text' });
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006cd4]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}
        >
          {notification.message}
        </div>
      )}

      <div className="mb-6">
        <p className="text-gray-400">
          {t('leadFields.description') ||
            'Configure what data the chatbot collects from your website visitors. Choose a template or create custom fields.'}
        </p>
      </div>

      {/* Templates */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          {t('leadFields.templates') || 'Templates'}
        </h2>
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-2">
              {t('leadFields.selectTemplate') || 'Select a business template'}
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4] text-sm"
            >
              <option value="">{t('leadFields.chooseTemplate') || 'Choose a template...'}</option>
              {templates.map((template) => (
                <option key={template.template_key} value={template.template_key}>
                  {getTemplateDisplayName(template)}
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <p className="text-sm text-gray-400 mt-2">
                {getTemplateDescription(templates.find(t => t.template_key === selectedTemplate)!)}
              </p>
            )}
          </div>
          <button
            onClick={handleApplyTemplate}
            disabled={!selectedTemplate || saving}
            className="px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {saving ? t('common.saving') || 'Saving...' : t('leadFields.apply') || 'Apply'}
          </button>
        </div>
      </div>

      {/* Template Apply Confirmation */}
      {showTemplateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#1a1a25] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('leadFields.applyTemplateConfirmTitle') || 'Apply template?'}
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              {t('leadFields.applyTemplateConfirmDesc') || 'This will replace all current fields with the selected template. Any custom edits will be lost.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTemplateConfirm(false)}
                className="flex-1 px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={confirmApplyTemplate}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {saving ? t('common.saving') || 'Saving...' : t('leadFields.applyTemplateConfirmButton') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Fields */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            {t('leadFields.currentFields') || 'Current Fields'} ({fields.length})
          </h2>
          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-[#006cd4]/20 text-[#006cd4] border border-[#006cd4]/20 rounded-lg hover:bg-[#006cd4]/30 transition-colors flex items-center gap-2"
          >
            <Icon name="plus" className="w-4" />
            {t('leadFields.addField') || 'Add Field'}
          </button>
        </div>

        {fields.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Icon name="clipboard-list" className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('leadFields.noFields') || 'No fields configured. Apply a template or add custom fields.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={field.key}
                className="flex items-center gap-4 p-4 bg-[#0a0a0f] border border-white/5 rounded-lg hover:border-white/10 transition-colors"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleMoveField(index, 'up')}
                    disabled={index === 0}
                    className="text-gray-500 hover:text-white disabled:opacity-30"
                  >
                    <Icon name="chevron-up" className="w-3" />
                  </button>
                  <button
                    onClick={() => handleMoveField(index, 'down')}
                    disabled={index === fields.length - 1}
                    className="text-gray-500 hover:text-white disabled:opacity-30"
                  >
                    <Icon name="chevron-down" className="w-3" />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-white">{field.label}</span>
                    {field.required && (
                      <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
                        {t('leadFields.required') || 'Required'}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded">
                      {categories.find(c => c.value === field.category)?.label || field.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 truncate">{field.prompt}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">Key: {field.key}</span>
                    <span className="text-xs text-gray-500">Type: {fieldTypes.find(t => t.value === field.type)?.label || field.type}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(field)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    title={t('common.edit') || 'Edit'}
                  >
                    <Icon name="pencil" className="w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteField(field.key)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title={t('common.delete') || 'Delete'}
                  >
                    <Icon name="trash" className="w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#1a1a25] border border-white/10 rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingField ? t('leadFields.editField') || 'Edit Field' : t('leadFields.addField') || 'Add Field'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingField(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <Icon name="xmark" className="w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {t('leadFields.fieldKey') || 'Field Key'} *
                </label>
                <input
                  type="text"
                  value={newField.key}
                  onChange={(e) => setNewField({ ...newField, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="e.g., product_type"
                  className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4]"
                  disabled={!!editingField}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('leadFields.keyHelp') || 'Unique identifier, lowercase with underscores'}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {t('leadFields.fieldLabel') || 'Label'} *
                </label>
                <input
                  type="text"
                  value={newField.label}
                  onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                  placeholder="e.g., Product Type"
                  className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4]"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {t('leadFields.fieldPrompt') || 'AI Prompt'} *
                </label>
                <textarea
                  value={newField.prompt}
                  onChange={(e) => setNewField({ ...newField, prompt: e.target.value })}
                  placeholder="e.g., What type of product is the customer looking for"
                  rows={3}
                  className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4] resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('leadFields.promptHelp') || 'Instructions for the AI on what to extract'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {t('leadFields.fieldCategory') || 'Category'}
                  </label>
                  <select
                    value={newField.category}
                    onChange={(e) => setNewField({ ...newField, category: e.target.value })}
                    className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4]"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {t('leadFields.fieldType') || 'Type'}
                  </label>
                  <select
                    value={newField.type}
                    onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                    className="w-full bg-[#0a0a0f] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#006cd4]"
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={newField.required}
                  onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                  className="w-4 h-4 rounded border-white/10 bg-[#0a0a0f] text-[#006cd4] focus:ring-[#006cd4]"
                />
                <label htmlFor="required" className="text-sm text-gray-300">
                  {t('leadFields.required') || 'Required field'}
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingField(null);
                  }}
                  className="flex-1 px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleSaveField}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                >
                  {saving
                    ? t('common.saving') || 'Saving...'
                    : editingField
                      ? t('common.update') || 'Update'
                      : t('common.create') || 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
